/**
 * 0.36.511 — Canonical Henaket FileUpload component.
 *
 * Anatomy (Desktop / Label / Hint / no-Error variant from the
 * Henaket spec):
 *   Container (630px wide)
 *   ├── Content (flex column, gap 4px, padding-bottom 16px)
 *   │   ├── Label   — H4 (Noto Sans Armenian 20px/32px 600)
 *   │   └── Hint    — Help Text (16px/24px 400) color #454545
 *   ├── Error       — visible only when `error` prop set; 5px inset
 *   │                 red-bar left decoration on the container in
 *   │                 error state (matches per-field error pattern)
 *   └── Upload row (416px × 30px)
 *       ├── Button "Choose file" (99px × 30px, F5F5F5 bg, C1C0C0 border)
 *       └── Filename text (283px, or "No file chosen" placeholder)
 *
 * Props:
 *   label        — optional H4 shown above; omit for no label
 *   hint         — optional help text below label; omit for no hint
 *   error        — optional string; when set, red-bar decoration + error text
 *   accept       — MIME/extension filter passed to native input
 *   disabled     — pass-through
 *   value        — File | null (controlled)
 *   onChange     — (file: File | null) => void
 *   buttonLabel  — default "Choose file"
 *   noFileText   — default "No file chosen"
 *   id           — optional id for label association
 *
 * The container's max-width is 630px per the Henaket spec, but it
 * shrinks below that when the parent is narrower (max-width, not
 * fixed).
 */
// 0.42.08 - useState for the component's own limit error.
import { useId, useLayoutEffect, useRef, useState } from 'react';
import styles from './FileUpload.module.css';
// 0.42.32 - The chosen files are listed in the canonical Henaket table,
// each with a canonical danger Button to remove it.
import PageTable from './PageTable';
import Tag from './Tag';
import Button from './Button';

/* 0.42.32 - A file's size for the table: KB below a megabyte, MB
 * above, one decimal. Pure, so it lives outside the component. */
function formatSize(bytes, kb = 'KB', mb = 'MB') {
  // 0.42.37 - The unit words are passed in, no longer written here.
  const n = Number(bytes) || 0;
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))} ${kb}`;
  return `${(n / (1024 * 1024)).toFixed(1)} ${mb}`;
}

/* 0.46.66 - A chosen file's name in its column: wrapped over up to two
 * lines. When it needs more, it is shortened to as much of its
 * beginning as fits, then "…" and the extension, which always shows
 * (report-2026-final-….pdf). The full name is the tooltip.
 *
 * Measured, not guessed: a hidden copy the column's width tries
 * shorter beginnings until the text fits two lines, again whenever the
 * column changes width. */
/* 0.48.59 - A file name shortened to one line of `maxPx`: its start,
   "…" and the extension (passport_scan_2026_….pdf). Measured with the
   tag's own font, so the cut is exact. */
let measureCtx = null;
function shortName(name, maxPx, font) {
  if (!maxPx || maxPx <= 0) return name;
  if (!measureCtx && typeof document !== 'undefined') {
    measureCtx = document.createElement('canvas').getContext('2d');
  }
  if (!measureCtx) return name;
  measureCtx.font = font;
  const w = (t) => measureCtx.measureText(t).width;
  if (w(name) <= maxPx) return name;
  const dot = name.lastIndexOf('.');
  const hasExt = dot > 0 && name.length - dot <= 10;
  const base = hasExt ? name.slice(0, dot) : name;
  const ext = hasExt ? name.slice(dot) : '';
  let lo = 0;
  let hi = base.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    if (w(`${base.slice(0, mid)}…${ext}`) <= maxPx) lo = mid; else hi = mid - 1;
  }
  return `${base.slice(0, lo)}…${ext}`;
}

/* The chosen files as tags, never wider than the element: each name is
   shortened to what fits beside the tag's padding and x. */
function FileTags({ files, disabled, removeLabel, onRemove, sizeOf }) {
  const boxRef = useRef(null);
  const [width, setWidth] = useState(0);
  const [font, setFont] = useState('600 16px sans-serif');
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;
    const read = () => {
      const cs = window.getComputedStyle(box);
      setWidth(box.clientWidth - parseFloat(cs.paddingLeft || 0) - parseFloat(cs.paddingRight || 0));
    };
    const fam = window.getComputedStyle(box).fontFamily || 'sans-serif';
    setFont(`600 16px ${fam}`);
    read();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(read) : null;
    if (ro) ro.observe(box);
    return () => { if (ro) ro.disconnect(); };
  }, []);
  // Tag: 8 + 8 padding, 2 border, 8 gap and the 16px x (none when disabled).
  const room = Math.max(40, width - 18 - (disabled ? 0 : 24));
  return (
    <div ref={boxRef} className={styles.fileTags}>
      {files.map((f, i) => (
        <Tag key={`${f.name}-${f.size}-${i}`} variant="outlined" closable={!disabled}
             className={styles.fileTag}
             title={`${f.name} (${sizeOf(f)})`}
             closeAriaLabel={`${removeLabel} ${f.name}`}
             onClose={() => onRemove(i)}>
          {shortName(String(f.name || ''), room, font)}
        </Tag>
      ))}
    </div>
  );
}

function FileName({ name }) {
  const boxRef = useRef(null);
  const probeRef = useRef(null);
  const [shown, setShown] = useState(name);
  useLayoutEffect(() => {
    const box = boxRef.current;
    const probe = probeRef.current;
    if (!box || !probe) return undefined;
    const dot = name.lastIndexOf('.');
    const hasExt = dot > 0 && name.length - dot <= 10;
    const base = hasExt ? name.slice(0, dot) : name;
    const ext = hasExt ? name.slice(dot) : '';
    const fit = () => {
      const lh = parseFloat(window.getComputedStyle(probe).lineHeight) || 24;
      const max = lh * 2 + 1;
      const fits = (t) => { probe.textContent = t; return probe.offsetHeight <= max; };
      if (fits(name)) { setShown(name); return; }
      let lo = 0;
      let hi = base.length;
      while (lo < hi) {
        const mid = Math.ceil((lo + hi) / 2);
        if (fits(`${base.slice(0, mid)}…${ext}`)) lo = mid; else hi = mid - 1;
      }
      setShown(`${base.slice(0, lo)}…${ext}`);
    };
    fit();
    let lastWidth = box.clientWidth;
    const ro = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(() => {
        if (box.clientWidth !== lastWidth) { lastWidth = box.clientWidth; fit(); }
      })
      : null;
    if (ro) ro.observe(box);
    return () => { if (ro) ro.disconnect(); };
  }, [name]);
  return (
    <span ref={boxRef} className={styles.fileName} title={name}>
      {shown}
      <span ref={probeRef} className={styles.fileNameProbe} aria-hidden="true" />
    </span>
  );
}

export default function FileUpload({
  label,
  hint,
  error,
  accept,
  disabled = false,
  value = null,
  onChange,
  /* 0.42.08 - Limits, checked when a file is chosen.
   *
   * The file never leaves the browser - this element publishes its NAME
   * into the form values, as its own comment says - so these are the
   * only place they can be enforced. That makes them a help to an
   * honest citizen rather than a security control, and they are worded
   * as such.
   *
   * maxSizeMb        0 = no limit
   * maxMegapixels    images only; width x height / 1e6
   * maxDimensionPx   images only; the LONGER side
   */
  /* 0.42.11 - More than one file. The value becomes an ARRAY when set,
   * and stays a single File when not, so nothing that already renders
   * one file has to change. */
  /* 0.42.13 - The admin's own wording for a limit failure, with
   * placeholders for the numbers. Empty falls back to the built-in
   * sentences. */
  limitMessage = '',
  /* 0.42.50 - Per-kind messages: { type, size, dimension, megapixels,
     count }. Any left out falls back to limitMessage, then the built-in. */
  limitMessages = null,
  /* 0.42.32 - Text of each row's Remove button. */
  removeLabel = 'Remove',
  /* 0.42.38 - What the row says once files are chosen. {uploaded_count}
     is filled here, with how many. */
  uploadedText = '{uploaded_count} file(s) uploaded',
  /* 0.42.37 - The unit words for the size column, resolved by the
     renderer so they can come from a library in the viewer's language. */
  sizeUnitKb = 'KB',
  sizeUnitMb = 'MB',
  multiple = false,
  /* 0.48.58 - How the chosen files are listed: 'table' (name, size and
     a Remove button per row) or 'tags' (each name as a Henaket tag with
     its x, on one wrapping line). */
  listStyle = 'table',
  /* 0.42.43 - With several files allowed: how many at most. 0 = no
     limit. Every file is held in the page as text, so an unbounded list
     is an unbounded page. */
  maxFiles = 0,
  maxSizeMb = 0,
  maxMegapixels = 0,
  maxDimensionPx = 0,
  buttonLabel = 'Choose file',
  noFileText = 'No file chosen',
  constraint,
  id,
}) {
  const inputRef = useRef(null);
  const generatedId = useId();
  const inputId = id || generatedId;

  const handleButtonClick = () => {
    if (disabled) return;
    inputRef.current?.click();
  };

  /* 0.42.08 - A limit failure is the component's OWN error, distinct
   * from the `error` prop a rule or validator supplies. Kept separate
   * so neither erases the other; both render, the local one first
   * because it describes what just happened. */
  const [localError, setLocalError] = useState('');

  /* 0.42.11 - Check every chosen file, not just the first.
   *
   * Each is measured on its own, and a failure names WHICH file: with
   * four selected, "This file is 9 MB" tells the citizen nothing about
   * which one to drop.
   *
   * All or nothing. Silently keeping the files that passed would leave
   * somebody believing all four went through, and the row would say
   * "3 files" after they picked four - a difference nobody reads. */
  const handleChange = (e) => {
    const files = Array.from(e.target.files || []);
    /* 0.42.32 - Reset the native input once its files are read. The
       Files are held in state from here on, and a browser fires no
       change event for an IDENTICAL selection - so without this, a
       citizen who removed a file could not choose it again. */
    e.target.value = '';
    // 0.42.43 - A choice of nothing changes nothing when files are
    // appended: the list already built must not vanish.
    if (files.length === 0) { if (!multiple) onChange?.(null); return; }

    const problems = [];
    // 0.42.43 - Which files failed, so a multi-file choice can keep the
    // rest. A single-file choice still treats any failure as the whole.
    const rejected = new Set();
    /* 0.42.13 - What the template can refer to. The limits are known up
       front; the uploaded numbers are filled by whichever check fails
       first, because one message describes one failure - naming four
       files' dimensions in one sentence helps nobody. */
    const facts = {
      // 0.42.14 - Without the dots, so the message reads "pdf, docx"
      // rather than ".pdf, .docx" - the admin adds whatever punctuation
      // the sentence needs.
      allowed_extensions: (accept || '').split(',')
        .map(x => x.trim().replace(/^\./, '')).filter(Boolean).join(', '),
      allowed_size: maxSizeMb || '',
      allowed_resolution: maxMegapixels || '',
      allowed_long_side_size: maxDimensionPx || '',
    };
    /* 0.42.14 - Enforce the accept filter.
     *
     * `accept` only filters the browse dialog. A citizen who picks "All
     * files" can choose anything, and the platform took it - so a form
     * asking for a PDF accepted an .exe without a word.
     *
     * Matched on the EXTENSION and on the MIME type, because neither
     * alone is enough: a browser may report no type for an unusual
     * format, and a file renamed .pdf still carries its real type. A
     * match on either is accepted; the check is a guard against
     * mistakes, not against a determined attacker, who is not stopped
     * by anything the browser does.
     */
    const acceptRules = (accept || '').split(',')
      .map(x => x.trim().toLowerCase()).filter(Boolean);
    const extensionsAllowed = acceptRules.filter(r => r.startsWith('.'));

    const fileAllowed = (file) => {
      if (acceptRules.length === 0) return true;
      const name = (file.name || '').toLowerCase();
      const dot = name.lastIndexOf('.');
      const ext = dot === -1 ? '' : name.slice(dot);
      const type = (file.type || '').toLowerCase();
      return acceptRules.some((rule) => {
        if (rule.startsWith('.')) return ext === rule;
        if (rule.endsWith('/*')) return type.startsWith(rule.slice(0, -1));
        return type === rule;
      });
    };

    const measure = (file) => {
      if (!fileAllowed(file)) {
        const name = (file.name || '').toLowerCase();
        const dot = name.lastIndexOf('.');
        const ext = dot === -1 ? '' : name.slice(dot + 1);
        if (facts.uploaded_extension === undefined) {
          facts.uploaded_extension = ext;
          if (facts.file_name === undefined) facts.file_name = file.name;
        }
        problems.push({ kind: 'type', text: files.length > 1
          ? `${file.name} is not an accepted kind of file.`
          : 'That is not an accepted kind of file.',
          facts: { ...facts, file_name: file.name, uploaded_extension: ext } });
        rejected.add(file);
        return;
      }
      if (maxSizeMb > 0 && file.size > maxSizeMb * 1024 * 1024) {
        const mb = (file.size / (1024 * 1024)).toFixed(1);
        if (facts.uploaded_size === undefined) {
          facts.uploaded_size = mb;
          facts.file_name = file.name;
        }
        problems.push({ kind: 'size', text: files.length > 1
          ? `${file.name} is ${mb} MB. The limit is ${maxSizeMb} MB.`
          : `This file is ${mb} MB. The limit is ${maxSizeMb} MB.`,
          facts: { ...facts, file_name: file.name, uploaded_size: mb } });
        rejected.add(file);
      }
    };
    files.forEach(measure);

    const needPixels = (maxMegapixels > 0 || maxDimensionPx > 0)
      && files.some(f => f.type.startsWith('image/'));
    if (!needPixels) { finish(files, problems, facts, rejected); return; }

    // Every image decoded before deciding, so one message can name all
    // the problems rather than the citizen fixing them one at a time.
    const images = files.filter(f => f.type.startsWith('image/'));
    let pending = images.length;
    const done = () => { if (--pending <= 0) finish(files, problems, facts, rejected); };

    images.forEach((file) => {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(url);
        const longest = Math.max(img.naturalWidth, img.naturalHeight);
        const mp = (img.naturalWidth * img.naturalHeight) / 1000000;
        const who = files.length > 1 ? `${file.name} is` : 'This image is';
        if (facts.uploaded_resolution === undefined
            && ((maxDimensionPx > 0 && longest > maxDimensionPx)
              || (maxMegapixels > 0 && mp > maxMegapixels))) {
          facts.uploaded_resolution = mp.toFixed(1);
          facts.uploaded_long_side_size = longest;
          if (facts.file_name === undefined) facts.file_name = file.name;
        }
        if (maxDimensionPx > 0 && longest > maxDimensionPx) {
          problems.push({ kind: 'dimension', text: `${who} ${img.naturalWidth}x${img.naturalHeight}. `
            + `The longest side may be ${maxDimensionPx} pixels.`,
            facts: { ...facts, file_name: file.name, uploaded_long_side_size: longest,
                     uploaded_resolution: mp.toFixed(1) } });
          rejected.add(file);
        }
        if (maxMegapixels > 0 && mp > maxMegapixels) {
          problems.push({ kind: 'megapixels', text: `${who} ${mp.toFixed(1)} megapixels. `
            + `The limit is ${maxMegapixels}.`,
            facts: { ...facts, file_name: file.name, uploaded_resolution: mp.toFixed(1) } });
          rejected.add(file);
        }
        done();
      };
      // Undecodable: left to the accept filter rather than reported as
      // oversized, which would be a guess.
      img.onerror = () => { URL.revokeObjectURL(url); done(); };
      img.src = url;
    });
  };

  /* 0.42.13 - Fill the admin's template.
   *
   * Numbers only, no units: the admin writes "Maximum {allowed_size} MB"
   * and chooses how to say MB in each language. A unit baked into the
   * value would be untranslatable and would read wrongly in Armenian,
   * where it follows the number differently.
   *
   * A placeholder whose value is unknown - resolution, when a PDF broke
   * the size limit - renders EMPTY rather than as the literal token. A
   * citizen should never see the platform's own syntax.
   */
  const fillTemplate = (tpl, facts) => tpl.replace(
    /\{(uploaded_size|uploaded_resolution|uploaded_long_side_size|uploaded_extension|allowed_size|allowed_resolution|allowed_long_side_size|allowed_extensions|allowed_count|uploaded_count|file_name)\}/g,
    (_, key) => (facts[key] === undefined || facts[key] === null
      ? '' : String(facts[key])));

  /* 0.42.50 - One message per KIND of failure, in the admin's words.
   *
   *   type  size  dimension  megapixels  count
   *
   * Each looks for its own message first, then "Message when a limit is
   * exceeded", then the English built-in. Before, the single message
   * replaced all five, so a translated form had one sentence for every
   * failure and could not say whether a file was too big or the wrong
   * type.
   *
   * Each problem is filled with ITS OWN file's numbers. They used to
   * share the first failure's, so a second failing file was described
   * with the first one's size. Identical sentences - a message without
   * a per-file placeholder, repeated for several files - appear once. */
  const showProblems = (problems) => {
    if (problems.length === 0) { setLocalError(''); return; }
    const lines = [];
    for (const pr of problems) {
      const tpl = (limitMessages && limitMessages[pr.kind]) || limitMessage;
      const line = tpl ? fillTemplate(tpl, pr.facts || {}) : pr.text;
      if (line && !lines.includes(line)) lines.push(line);
    }
    setLocalError(lines.join(' '));
  };

  const finish = (files, problems, facts, rejected = new Set()) => {
    // One file: a new choice REPLACES, and any failure refuses it whole.
    // Unchanged, and right for a field that holds a single document:
    // keeping a file the form will reject is worse than clearing it.
    if (!multiple) {
      if (problems.length > 0) {
        showProblems(problems);
        onChange?.(null);
        return;
      }
      setLocalError('');
      onChange?.(files[0] || null);
      return;
    }

    /* 0.42.43 - Several files: a new choice is ADDED to the list.
     *
     * Replacing it lost earlier files without a word: a citizen who
     * attached a passport scan and went back for their diploma found
     * only the diploma. People gather documents one at a time, from
     * different folders, so each choice adds.
     *
     *   duplicates  same name, size and date - skipped quietly, since the
     *               table already shows each file once
     *   failures    files that broke a limit are left out and NAMED; the
     *               rest are kept. With every file listed by name, a
     *               partial result is visible, not a silent "3 of 4"
     *   the cap     beyond Maximum number of files, the NEWEST are left
     *               out; what is already attached stays
     */
    const key = (f) => `${f.name}|${f.size}|${f.lastModified}`;
    const merged = [...chosen];
    const seen = new Set(merged.map(key));
    for (const f of files) {
      if (rejected.has(f)) continue;
      const k = key(f);
      if (seen.has(k)) continue;
      seen.add(k);
      merged.push(f);
    }
    let kept = merged;
    const allProblems = [...problems];
    if (maxFiles > 0 && merged.length > maxFiles) {
      kept = merged.slice(0, maxFiles);
      facts.allowed_count = maxFiles;
      facts.uploaded_count = merged.length;
      allProblems.push({ kind: 'count', text: `Up to ${maxFiles} files may be attached.`,
        facts: { ...facts, allowed_count: maxFiles, uploaded_count: merged.length } });
    }
    showProblems(allProblems);

    // Only when the list really changed. Republishing an unchanged list
    // would clear and refill it, and a rule on "is filled" would fire a
    // second time - making the same API call again.
    const changed = kept.length !== chosen.length
      || kept.some((f, i) => key(f) !== key(chosen[i]));
    if (changed) onChange?.(kept.length > 0 ? kept : null);
  };

  /* 0.42.11 - One name, or "3 files" with them listed beneath. Naming
     every file in the row would push the constraint hint off the line
     the moment somebody picks four. */
  const chosen = Array.isArray(value) ? value : (value ? [value] : []);

  /* 0.42.32 - Remove one file.
   *
   * Straight to the parent, NOT through the limit checks: taking a file
   * away cannot break a limit, and re-running the checks would re-read
   * every remaining image for nothing. The parent's pipeline then
   * republishes everything about what is left, name last.
   *
   * A single-file upload hands back null, a multiple one the shortened
   * list - or null once it is empty, so "is filled" goes false. */
  const removeAt = (index) => {
    const rest = chosen.filter((_, k) => k !== index);
    setLocalError('');
    if (inputRef.current) inputRef.current.value = '';
    if (multiple) onChange?.(rest.length > 0 ? rest : null);
    else onChange?.(null);
  };

  const filename = chosen.length === 1
    ? chosen[0].name
    : (chosen.length > 1 ? `${chosen.length} files` : '');

  return (
    <div className={`${styles.container} ${(error || localError) ? styles.hasError : ''} ${disabled ? styles.isDisabled : ''}`}>
      {(label || hint) && (
        <div className={styles.content}>
          {label && (
            <label htmlFor={inputId} className={styles.label}>{label}</label>
          )}
          {hint && (
            <div className={styles.hint}>{hint}</div>
          )}
        </div>
      )}
      {localError && (
        <div className={styles.error} role="alert">{localError}</div>
      )}
      {error && (
        <div className={styles.error} role="alert">{error}</div>
      )}
      {/* 0.46.64 - The main part, which a line aligns by. */}
      <div className={styles.uploadRow} data-main="">
        <button
          type="button"
          className={styles.button}
          disabled={disabled}
          onClick={handleButtonClick}
        >
          {buttonLabel}
        </button>
        {/* 0.42.32 - Only the "nothing chosen" text stays in the row;
            once files are chosen the table below names them, and saying
            it twice would push the constraint hint off the line. */}
        {/* 0.42.38 - Nothing chosen: the "no file" text. Files chosen:
            the admin's count text, e.g. "բեռնված է 2 ֆայլ". Every
            occurrence of {uploaded_count} is replaced, so it may appear
            more than once in a sentence. */}
        {chosen.length === 0 ? (
          <span className={styles.noFile}>{noFileText}</span>
        ) : (uploadedText ? (
          <span className={styles.filename}>
            {String(uploadedText).split('{uploaded_count}').join(String(chosen.length))}
          </span>
        ) : null)}
        {/* 0.39.16 - Optional constraint hint (e.g. "(Max. 5mb .pdf
            or .ppt)") shown after the filename, per the spec. */}
        {/* 0.42.11 - With several files the row says "3 files"; the
            names go underneath, where a long list can wrap without
            pushing the constraint hint off the line. */}
        {constraint && (
          <span className={styles.constraint}>{constraint}</span>
        )}
        {/* Native input is off-screen; the button triggers it.
         *
         * 0.42.80 - and it is out of the tab order. Off-screen is not
         * unfocusable: a keyboard user reached the invisible input
         * first and then the button, so the control appeared to take
         * two presses of Tab and one of them landed on nothing.
         *
         * The visible button is a real button that clicks this input,
         * and it carries the label, so nothing is lost by taking this
         * one out of the tab order. */}
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={handleChange}
          className={styles.nativeInput}
          tabIndex={-1}
          aria-hidden="true"
        />
      </div>
      {/* 0.42.32 - The chosen files, as Henaket specifies: the
          canonical table with no header row and no vertical lines,
          one row per file - name, size, and a red Remove aligned to
          the right. The first two columns are 195px. */}
      {chosen.length > 0 && listStyle === 'tags' && (
        <FileTags files={chosen} disabled={disabled} removeLabel={removeLabel}
                  onRemove={removeAt}
                  sizeOf={(f) => formatSize(f.size, sizeUnitKb, sizeUnitMb)} />
      )}
      {chosen.length > 0 && listStyle !== 'tags' && (
        <div className={styles.fileTable}>
          <PageTable
            name={`${inputId}-files`}
            headers={['', '', '']}
            showHeader={false}
            horizontalBordersOnly
            sortable={false}
            fixedLayout
            colWidths={[195, 195, null]}
            width="100%"
            rowKeys={chosen.map((f, i) => `${f.name}-${f.size}-${i}`)}
            rows={chosen.map((f, i) => [
              /* 0.46.66 - The name wraps over up to 2 lines; a longer
                 one keeps its beginning, "...", and the extension (see
                 FileName). It used to run over the size column. */
              <FileName key="name" name={String(f.name || '')} />,
              formatSize(f.size, sizeUnitKb, sizeUnitMb),
              <div key="remove" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="danger"
                  disabled={disabled}
                  aria-label={`${removeLabel} ${f.name}`}
                  onClick={() => removeAt(i)}
                >
                  {removeLabel}
                </Button>
              </div>,
            ])}
          />
        </div>
      )}
    </div>
  );
}
